import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Zap, Terminal } from 'lucide-react';

const CodeGeneration = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Code Generation</h1>
          <p className="text-lg text-muted-foreground">
            Use UIKit CLI to generate pages, components, and features automatically.
          </p>
        </div>

        <Alert>
          <Zap className="w-4 h-4" />
          <AlertDescription>
            Code generation speeds up development by creating boilerplate files with consistent patterns.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Available Generators</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Command</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npx uikit generate page</td>
                  <td className="p-3 text-muted-foreground">Generate a new page</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npx uikit generate component</td>
                  <td className="p-3 text-muted-foreground">Generate a component</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">npx uikit generate feature</td>
                  <td className="p-3 text-muted-foreground">Generate a feature module (FBCA)</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">npx uikit generate hook</td>
                  <td className="p-3 text-muted-foreground">Generate a custom hook</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Terminal className="w-6 h-6" />
            Generate Page
          </h2>

          <CodePreview
            title="Basic Usage"
            code={`# Generate a page in SPA/Multi template
npx uikit generate page about

# Generate in a specific directory
npx uikit generate page dashboard/analytics

# With specific layout
npx uikit generate page settings --layout admin`}
            language="bash"
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Generated Files</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg">
{`src/pages/about.tsx
# or with path:
src/pages/dashboard/analytics.tsx`}
              </pre>
            </CardContent>
          </Card>

          <CodePreview
            title="Generated Page Code"
            code={`// src/pages/about.tsx
import { PageLayout } from '@voilajsx/uikit/page-layout';

function AboutPage() {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold mb-4">About</h1>
        <p className="text-muted-foreground">
          Page content goes here.
        </p>
      </div>
    </PageLayout>
  );
}

export default AboutPage;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Generate Component</h2>

          <CodePreview
            title="Component Generation"
            code={`# Generate a shared component
npx uikit generate component UserCard

# Generate in a feature (FBCA)
npx uikit generate component UserCard --feature dashboard

# With props interface
npx uikit generate component UserCard --props`}
            language="bash"
          />

          <CodePreview
            title="Generated Component"
            code={`// src/components/UserCard.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';

interface UserCardProps {
  // Add props here
}

export function UserCard({}: UserCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>UserCard</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Component content */}
      </CardContent>
    </Card>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Generate Feature (FBCA)</h2>

          <CodePreview
            title="Feature Generation"
            code={`# Generate a complete feature module
npx uikit generate feature blog

# This creates:
# src/web/features/blog/
#   ├── components/
#   │   └── .gitkeep
#   ├── pages/
#   │   └── index.tsx
#   └── hooks/
#       └── .gitkeep`}
            language="bash"
          />

          <CodePreview
            title="Generated Feature Index"
            code={`// src/web/features/blog/pages/index.tsx
import { PageLayout } from '@voilajsx/uikit/page-layout';

function BlogPage() {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold mb-4">Blog</h1>
        <p className="text-muted-foreground">
          Welcome to the blog feature.
        </p>
      </div>
    </PageLayout>
  );
}

export default BlogPage;`}
            language="tsx"
          />

          <Alert>
            <Zap className="w-4 h-4" />
            <AlertDescription>
              In FBCA, the generated feature is automatically available at <code>/blog</code> thanks to auto-discovery routing!
            </AlertDescription>
          </Alert>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Generate Hook</h2>

          <CodePreview
            title="Hook Generation"
            code={`# Generate a custom hook
npx uikit generate hook useLocalStorage

# Generate in shared
npx uikit generate hook useApi --shared

# Generate for a feature
npx uikit generate hook useBlogPosts --feature blog`}
            language="bash"
          />

          <CodePreview
            title="Generated Hook"
            code={`// src/hooks/useLocalStorage.ts
import { useState, useEffect } from 'react';

export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue] as const;
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Generator Options</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Option</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--layout &lt;name&gt;</td>
                  <td className="p-3 text-muted-foreground">Specify layout (page, admin, auth)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--feature &lt;name&gt;</td>
                  <td className="p-3 text-muted-foreground">Generate inside feature folder</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--shared</td>
                  <td className="p-3 text-muted-foreground">Generate in shared folder</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--props</td>
                  <td className="p-3 text-muted-foreground">Include props interface</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">--dry-run</td>
                  <td className="p-3 text-muted-foreground">Preview without creating files</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Best Practices</h2>

          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">1.</span>
              <div>
                <strong className="text-foreground">Use consistent naming</strong> - PascalCase for components, camelCase for hooks
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">2.</span>
              <div>
                <strong className="text-foreground">Use --dry-run first</strong> - Preview what files will be created
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">3.</span>
              <div>
                <strong className="text-foreground">Organize by feature</strong> - In FBCA, use --feature to keep code colocated
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">4.</span>
              <div>
                <strong className="text-foreground">Customize generated code</strong> - Generators provide starting points, customize as needed
              </div>
            </li>
          </ul>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default CodeGeneration;
